import type { z } from 'zod';

/**
 * A declarative filter spec: declare each list-filter field once (its kind, and any
 * backend wire-param rename), and derive the four mappings the catalog features
 * previously hand-wrote five identical ways across api / drawer / page / schema:
 *
 *  - `toDefaults`  applied filters → RHF draft (enum/number → undefined, range → null, multi → [])
 *  - `toFilters`   RHF draft → applied filters (strip '' / null / empty arrays)
 *  - `toListQuery` applied filters → backend query params (join multi, rename via `param`)
 *  - `hasActiveFilters` applied filters → boolean (search excluded; ranges count a `0` bound)
 *
 * The empty-vs-`undefined` rule — the source of recurring cleared-select bugs — now lives
 * in exactly one place, keyed on field kind. Range fields follow the `${base}From`/`${base}To`
 * key convention; `search` (kind `text`) participates only in `toListQuery`.
 *
 * The zod field-schema stays hand-written per feature (it carries the per-field types and
 * the range-error message); its `superRefine` reuses {@link enforceRangePairs}.
 */

type SingleKind = 'text' | 'enum' | 'multi' | 'number';

type SingleField = { kind: SingleKind; key: string; param?: string };
type RangeFieldDef = { kind: 'range'; base: string; param?: string };
export type FilterField = SingleField | RangeFieldDef;

/** Free-text search; emitted only into the query params, never the drawer draft. */
export const text = (key: string, param?: string): FilterField => ({ kind: 'text', key, param });
/** Single-select enum (e.g. status, tier); `''`/undefined are "unselected". */
export const enumField = (key: string, param?: string): FilterField => ({
  kind: 'enum',
  key,
  param,
});
/** Multi-select id list; joined to a comma string for the wire, `[]` is "none". */
export const multiField = (key: string, param?: string): FilterField => ({
  kind: 'multi',
  key,
  param,
});
/** Single numeric select with a falsy sentinel (e.g. freeSubscriptionPlan); `0`/`''` are "unselected". */
export const numberField = (key: string, param?: string): FilterField => ({
  kind: 'number',
  key,
  param,
});
/** A From/To numeric range over `${base}From`/`${base}To`; `param` renames the wire params. */
export const rangeField = (base: string, opts?: { param?: string }): FilterField => ({
  kind: 'range',
  base,
  param: opts?.param,
});

type Query = Record<string, string | number | undefined>;
type Rec = Record<string, unknown>;

/**
 * @typeParam Applied - the feature's applied-filter contract (`XListFilters`).
 * @typeParam Form - the feature's RHF draft contract (`XFilterFormValues`).
 */
export function defineFilterSpec<Applied, Form>(fields: readonly FilterField[]) {
  function toDefaults(applied: Applied): Form {
    const a = applied as Rec;
    const out: Rec = {};
    for (const f of fields) {
      if (f.kind === 'text') continue; // search is not a drawer field
      if (f.kind === 'enum' || f.kind === 'number') out[f.key] = a[f.key] || undefined;
      else if (f.kind === 'multi') out[f.key] = (a[f.key] as unknown[] | undefined) ?? [];
      else if (f.kind === 'range') {
        out[`${f.base}From`] = (a[`${f.base}From`] as number | null | undefined) ?? null;
        out[`${f.base}To`] = (a[`${f.base}To`] as number | null | undefined) ?? null;
      }
    }
    return out as Form;
  }

  function toFilters(form: Form): Applied {
    const v = form as Rec;
    const out: Rec = {};
    for (const f of fields) {
      if (f.kind === 'text') continue;
      if (f.kind === 'enum' || f.kind === 'number') out[f.key] = v[f.key] || undefined;
      else if (f.kind === 'multi') {
        const arr = v[f.key] as string[];
        out[f.key] = arr.length ? arr : undefined;
      } else if (f.kind === 'range') {
        out[`${f.base}From`] = (v[`${f.base}From`] as number | null) ?? undefined;
        out[`${f.base}To`] = (v[`${f.base}To`] as number | null) ?? undefined;
      }
    }
    return out as Applied;
  }

  function toListQuery(applied: Applied): Query {
    const a = applied as Rec;
    const out: Query = {};
    for (const f of fields) {
      if (f.kind === 'multi') {
        const arr = a[f.key] as string[] | undefined;
        out[f.param ?? f.key] = arr?.length ? arr.join(',') : undefined;
      } else if (f.kind === 'range') {
        const base = f.param ?? f.base;
        out[`${base}From`] = (a[`${f.base}From`] as number | undefined) ?? undefined;
        out[`${base}To`] = (a[`${f.base}To`] as number | undefined) ?? undefined;
      } else {
        // text / enum / number all strip falsy (''/0/undefined) via ||.
        out[f.param ?? f.key] = (a[f.key] as string | number | undefined) || undefined;
      }
    }
    return out;
  }

  function hasActiveFilters(applied: Applied): boolean {
    const a = applied as Rec;
    return fields.some((f) => {
      if (f.kind === 'text') return false; // search does not count as an active filter
      if (f.kind === 'multi') return Boolean((a[f.key] as unknown[] | undefined)?.length);
      if (f.kind === 'range') return a[`${f.base}From`] != null || a[`${f.base}To`] != null;
      return Boolean(a[f.key]);
    });
  }

  return { toDefaults, toFilters, toListQuery, hasActiveFilters };
}

/**
 * Shared `superRefine` for filter range pairs: each `[from, to]` must satisfy `from <= to`
 * when both are set; the error is attached to the `to` path. Reused by every feature's
 * hand-written filter schema so the range rule has a single home.
 */
export function enforceRangePairs(pairs: readonly (readonly [string, string])[]) {
  return (values: Record<string, unknown>, ctx: z.RefinementCtx): void => {
    for (const [from, to] of pairs) {
      const lower = values[from];
      const upper = values[to];
      if (typeof lower === 'number' && typeof upper === 'number' && lower > upper) {
        ctx.addIssue({ code: 'custom', path: [to], message: 'From value cannot exceed To value' });
      }
    }
  };
}
